// Formulario crear/editar ingrediente. Incluye grid INCAP (kcal, prot, cho,
// grasa, sodio, K) — Sprint 8 final: persistido en backend.
import { useEffect, useState } from 'react'
import type {
  CategoriaAlimentoResponse,
  IngredienteCreateBody,
  IngredienteResponse,
  IngredienteUpdateBody,
  UnidadMedidaResponse,
} from '../../services/nutricionista'
import { nutricionistaApi } from '../../services/nutricionista'
import { ModalShell } from '../ui/ModalShell'

interface IngredienteFormProps {
  initial?: IngredienteResponse | null
  onSubmit: (body: IngredienteCreateBody | IngredienteUpdateBody) => Promise<void> | void
  onCancel: () => void
}

// Catalogo cerrado: los 14 alergenos del Codex Alimentarius. El valor se
// persiste como codigo canonico (MAYUSCULAS con _) para que el mismo alergeno
// no exista con escrituras distintas ("lactosa" vs "lacteo" duplicaba).
const ALERGENOS_CODEX: { codigo: string; etiqueta: string }[] = [
  { codigo: 'GLUTEN', etiqueta: 'Gluten (trigo y cereales)' },
  { codigo: 'CRUSTACEOS', etiqueta: 'Crustáceos (camarón, cangrejo)' },
  { codigo: 'HUEVO', etiqueta: 'Huevo' },
  { codigo: 'PESCADO', etiqueta: 'Pescado' },
  { codigo: 'MANI', etiqueta: 'Maní (cacahuete)' },
  { codigo: 'SOYA', etiqueta: 'Soya' },
  { codigo: 'LACTOSA', etiqueta: 'Lactosa / lácteos' },
  { codigo: 'FRUTOS_SECOS', etiqueta: 'Frutos secos (almendra, nuez)' },
  { codigo: 'APIO', etiqueta: 'Apio' },
  { codigo: 'MOSTAZA', etiqueta: 'Mostaza' },
  { codigo: 'AJONJOLI', etiqueta: 'Ajonjolí (sésamo)' },
  { codigo: 'SULFITOS', etiqueta: 'Sulfitos' },
  { codigo: 'ALTRAMUCES', etiqueta: 'Altramuces (lupino)' },
  { codigo: 'MOLUSCOS', etiqueta: 'Moluscos' },
]

interface FormState {
  codigo: string
  nombre: string
  unidad_default: string
  categoria: string
  es_alergeno: boolean
  tipo_alergeno: string
  activo: boolean
  kcal: string
  proteina_g: string
  cho_g: string
  grasa_g: string
  sodio_mg: string
  potasio_mg: string
}

const EMPTY: FormState = {
  codigo: '',
  nombre: '',
  unidad_default: '',
  categoria: '',
  es_alergeno: false,
  tipo_alergeno: '',
  activo: true,
  kcal: '',
  proteina_g: '',
  cho_g: '',
  grasa_g: '',
  sodio_mg: '',
  potasio_mg: '',
}

function fromResponse(r: IngredienteResponse): FormState {
  const numStr = (n: number | null | undefined): string =>
    n === null || n === undefined ? '' : String(n)
  return {
    ...EMPTY,
    codigo: r.codigo ?? '',
    nombre: r.nombre ?? '',
    unidad_default: r.unidad_default ?? '',
    categoria: r.categoria ?? '',
    es_alergeno: r.es_alergeno ?? false,
    tipo_alergeno: r.tipo_alergeno ?? '',
    activo: r.activo ?? true,
    kcal: numStr(r.kcal),
    proteina_g: numStr(r.proteina_g),
    cho_g: numStr(r.cho_g),
    grasa_g: numStr(r.grasa_g),
    sodio_mg: numStr(r.sodio_mg),
    potasio_mg: numStr(r.potasio_mg),
  }
}

function parseNum(v: string): number | null {
  if (v.trim() === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export default function IngredienteForm({ initial, onSubmit, onCancel }: IngredienteFormProps) {
  const editing = !!initial
  const [s, setS] = useState<FormState>(initial ? fromResponse(initial) : EMPTY)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unidades, setUnidades] = useState<UnidadMedidaResponse[]>([])
  const [categorias, setCategorias] = useState<CategoriaAlimentoResponse[]>([])

  // S8 T23: cargar catalogos dinamicos (unidades + categorias) desde backend.
  useEffect(() => {
    let cancelled = false
    Promise.allSettled([
      nutricionistaApi.listarUnidadesMedida(),
      nutricionistaApi.listarCategoriasAlimento(),
    ]).then(([uResp, cResp]) => {
      if (cancelled) return
      if (uResp.status === 'fulfilled') setUnidades(uResp.value)
      if (cResp.status === 'fulfilled') setCategorias(cResp.value)
    })
    return () => {
      cancelled = true
    }
  }, [])

  function update<K extends keyof FormState>(key: K, val: FormState[K]) {
    setS((prev) => ({ ...prev, [key]: val }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    // Sprint 8: el codigo se auto-genera en backend (ALI{N:03d}). El frontend
    // ya no exige codigo en creacion; se envia un placeholder que el backend ignora.
    if (!s.nombre.trim()) {
      setError('El nombre es obligatorio')
      return
    }
    setSubmitting(true)
    try {
      const base = {
        nombre: s.nombre.trim().toUpperCase(),  // S8: uppercase para uniformidad
        unidad_default: s.unidad_default.trim() || null,
        categoria: s.categoria.trim() || null,
        es_alergeno: s.es_alergeno,
        tipo_alergeno: s.tipo_alergeno.trim() || null,
        activo: s.activo,
      }
      const incap = {
        kcal: parseNum(s.kcal),
        proteina_g: parseNum(s.proteina_g),
        cho_g: parseNum(s.cho_g),
        grasa_g: parseNum(s.grasa_g),
        sodio_mg: parseNum(s.sodio_mg),
        potasio_mg: parseNum(s.potasio_mg),
      }
      if (editing) {
        await onSubmit({ ...base, ...incap } satisfies IngredienteUpdateBody)
      } else {
        const create: IngredienteCreateBody = {
          ...base,
          ...incap,
          codigo: 'AUTO',  // S8: backend ignora este valor y genera ALI{N:03d}
        }
        await onSubmit(create)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell
      title={editing ? 'Editar ingrediente' : 'Nuevo ingrediente'}
      onClose={onCancel}
      closeDisabled={submitting}
      maxWidth={640}
    >
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-semibold">Código</span>
              {editing ? (
                <input
                  type="text"
                  value={s.codigo}
                  disabled
                  className="mt-1 w-full border rounded px-2 py-1 text-sm uppercase bg-gray-100"
                  aria-label="Codigo del ingrediente"
                />
              ) : (
                <div className="mt-1 w-full border rounded px-2 py-1 text-sm bg-gray-50 text-gray-500 italic">
                  Se asigna automáticamente (ALInnn)
                </div>
              )}
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Nombre</span>
              <input
                type="text"
                value={s.nombre}
                onChange={(e) => update('nombre', e.target.value)}
                disabled={submitting}
                maxLength={100}
                aria-label="Nombre del ingrediente"
                className="mt-1 w-full border rounded px-2 py-1 text-sm uppercase"
                style={{ textTransform: 'uppercase' }}
              />
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Unidad default</span>
              <select
                value={s.unidad_default}
                onChange={(e) => update('unidad_default', e.target.value)}
                disabled={submitting}
                aria-label="Unidad default"
                className="mt-1 w-full border rounded px-2 py-1 text-sm bg-white"
              >
                <option value="">— Seleccionar —</option>
                {unidades.length === 0 && s.unidad_default && (
                  <option value={s.unidad_default}>{s.unidad_default}</option>
                )}
                {unidades.map((u) => (
                  <option key={u.id} value={u.codigo}>
                    {u.abreviatura} ({u.nombre})
                  </option>
                ))}
              </select>
              {unidades.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  Catálogo no cargado. Si persiste, pídele al admin que agregue unidades.
                </p>
              )}
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Categoría</span>
              <select
                value={s.categoria}
                onChange={(e) => update('categoria', e.target.value)}
                disabled={submitting}
                aria-label="Categoria"
                className="mt-1 w-full border rounded px-2 py-1 text-sm bg-white"
              >
                <option value="">— Seleccionar —</option>
                {categorias.length === 0 && s.categoria && (
                  <option value={s.categoria}>{s.categoria}</option>
                )}
                {categorias.map((c) => (
                  <option key={c.id} value={c.codigo}>
                    {c.nombre}
                  </option>
                ))}
              </select>
              {categorias.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  Catálogo no cargado. Si persiste, pídele al admin que agregue categorías.
                </p>
              )}
            </label>
          </div>

          <fieldset className="border rounded p-3">
            <legend className="text-sm font-semibold px-1">Alergenicidad</legend>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={s.es_alergeno}
                onChange={(e) => update('es_alergeno', e.target.checked)}
                aria-label="Es alergeno"
              />
              Es alergeno
            </label>
            {s.es_alergeno && (
              <label className="block mt-2">
                <span className="text-sm font-semibold">Tipo de alergeno</span>
                <select
                  value={s.tipo_alergeno}
                  onChange={(e) => update('tipo_alergeno', e.target.value)}
                  aria-label="Tipo de alergeno"
                  className="mt-1 w-full border rounded px-2 py-1 text-sm bg-white"
                >
                  <option value="">— Seleccionar —</option>
                  {/* Valor legado fuera del catalogo: mostrarlo para no perderlo al editar */}
                  {s.tipo_alergeno &&
                    !ALERGENOS_CODEX.some((a) => a.codigo === s.tipo_alergeno) && (
                      <option value={s.tipo_alergeno}>{s.tipo_alergeno} (legado)</option>
                    )}
                  {ALERGENOS_CODEX.map((a) => (
                    <option key={a.codigo} value={a.codigo}>
                      {a.etiqueta}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </fieldset>

          <fieldset className="border rounded p-3">
            <legend className="text-sm font-semibold px-1">Composicion INCAP (por 100 g)</legend>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {([
                ['kcal', 'kcal'],
                ['proteina_g', 'Proteina (g)'],
                ['cho_g', 'CHO (g)'],
                ['grasa_g', 'Grasa (g)'],
                ['sodio_mg', 'Sodio (mg)'],
                ['potasio_mg', 'Potasio (mg)'],
              ] as const).map(([key, label]) => (
                <label key={key} className="block">
                  <span className="text-xs font-semibold">{label}</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={s[key]}
                    onChange={(e) => update(key, e.target.value)}
                    disabled={submitting}
                    aria-label={label}
                    className="mt-1 w-full border rounded px-2 py-1 text-sm"
                  />
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Datos opcionales. Referencia: tabla INCAP, USDA FoodData Central, o etiqueta nutricional del producto.
            </p>
          </fieldset>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={s.activo}
              onChange={(e) => update('activo', e.target.checked)}
              aria-label="Activo"
            />
            Activo
          </label>

          {error && (
            <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="flex-1 border py-2 rounded text-sm disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-avante-primary text-white py-2 rounded text-sm font-semibold disabled:opacity-50"
            >
              {submitting ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear ingrediente'}
            </button>
          </div>
        </form>
    </ModalShell>
  )
}
